import {hasServerFlag, SERVER_FLAGS} from '../../../../../config';
import {RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {
  DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  LinePlotSettings,
} from '../../types';
import {
  DEFAULT_FEATURE_FLAG_FULL_FIDELITY_MAX_RUNS,
  DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING,
  DEFAULT_PANEL_MAX_NUM_RUNS,
} from '../maxRunDefaults';

export const getCascadingMaxRuns = ({
  canOverrideMaxRuns,
  pointVisualizationMethod,
  maxRuns,
  limit,
}: {
  canOverrideMaxRuns?: boolean;
  pointVisualizationMethod: DerivedPointVisualizationOption;
  maxRuns: LinePlotSettings['maxRuns'];
  limit?: RunsLinePlotConfig['limit'];
}) => {
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);

  const maxValue = getMaxRunLimitWithOverrideFlag({
    canOverrideMaxRuns: canOverrideMaxRuns ?? false,
    isBucketing,
  });

  const passedLimit = limit ?? maxRuns;
  if (passedLimit == null) {
    return undefined;
  }

  // if both are undefined above than we'll have a value here even if TS can't pick it up
  return Math.min(maxValue, (limit ?? maxRuns) as number);
};

/**
 * Determine the maximum number of runs a user can set for a line plot.
 *
 * First: if there is a server flag, we MUST respect that limit. Server can be a less performant environment for the purposes of line plots, so we want to make sure any valid limit set there cannot be overridden.
 *
 * Second: if there is the experimental ramp flag, then we can override the limit to whatever the value of DEFAULT_DEMO_MAX_RUNS is.
 *
 * Otherwise, we can fall back to the existing defaults.
 * 1. If it's bucketing, we're defaulting to 50, which is the default value of the server flag if one isn't defined.
 * 2. If it's not bucketing, than we're using the 300 value. This is higher than the 100 we support in sampling because of some edge cases around media iirc
 *
 * TODO: figure out what limits line plots in sampling
 */
export const getMaxRunLimitWithOverrideFlag = (
  {
    canOverrideMaxRuns,
    isBucketing,
  }: {
    canOverrideMaxRuns: boolean;
    isBucketing: boolean;
  },
  isLinePlot = false
) => {
  // these limits only apply to bucketing
  // sampling limits are exempt
  if (!isBucketing) {
    return isLinePlot
      ? DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING
      : DEFAULT_PANEL_MAX_NUM_RUNS;
  }
  // if it's FF fidelity it's a line plot AND the server limit applies
  const hasServerLimit = hasServerFlag(SERVER_FLAGS.maxRunsLimit);

  // this gets kind of weird - the default hardcoded value is a check against a config flag
  // ```
  // DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS = serverFlagMaxRunsLimit() // has a default if undefined
  // ```
  // this means that IF a server limit is configured for an environment, it will show up as this value
  // however since it's defaulted we have to check if the underlying config value exists before we use it
  // This way we can fall through when there's not an active server flag limit and check for the ramp flag
  // but then ultimately we'll end up returning the defaulted value
  const serverLimitOverride = hasServerLimit
    ? DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS
    : undefined;

  // if this value isn't undefined it's because there is a set server limit, and we'll use that
  if (serverLimitOverride) {
    return serverLimitOverride;
  }

  // otherwise allow the experimental ramp flag to override the limit
  if (canOverrideMaxRuns) {
    return DEFAULT_FEATURE_FLAG_FULL_FIDELITY_MAX_RUNS;
  }

  // finally just return the
  return DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS;
};

export const throttleLinePlotMaxRunsToConfigLimit = (
  {
    isBucketing,
    canOverrideMaxRuns,
  }: {
    isBucketing: boolean;
    canOverrideMaxRuns: boolean;
  },
  limitFromPanelConfig?: number
) => {
  const maxRunLimit = getMaxRunLimitWithOverrideFlag(
    {
      canOverrideMaxRuns,
      isBucketing,
    },
    true // this is a line plot
  );
  return throttleMaxRunsToLimit(limitFromPanelConfig, maxRunLimit);
};

export const throttleMaxRunsToLimit = (
  value: number | undefined,
  limit: number
) => {
  if (value) {
    return Math.min(value, limit);
  }
  return value;
};
